<script>
  import { route } from './lib/stores/router.svelte.js';
  import HomePage from './components/home/HomePage.svelte';
  import BinderView from './components/binder/BinderView.svelte';
  import SearchPage from './components/search/SearchPage.svelte';
  import Sidebar from './components/layout/Sidebar.svelte';
  import SelectedCardsDrawer from './components/binder/SelectedCardsDrawer.svelte';
  import TradeSplitDrawer from './components/trade/TradeSplitDrawer.svelte';
  import { isTradeMode } from './lib/stores/trade.svelte.js';
  import { initializeAuth, authState, isUserAnonymous } from './lib/stores/auth.svelte.js';
  import TradeNotification from './components/shared/TradeNotification.svelte';
  import { loadUserGroups } from './lib/stores/groups.svelte.js';
  import { loadSidebarData } from './lib/stores/sidebar.svelte.js';
  import { untrack } from 'svelte';
  import GroupsPage from './components/groups/GroupsPage.svelte';
  import GroupDetailPage from './components/groups/GroupDetailPage.svelte';
  import GroupJoinPage from './components/groups/GroupJoinPage.svelte';
  import GroupInvitePage from './components/groups/GroupInvitePage.svelte';
  import BindersPage from './components/binders/BindersPage.svelte';
  import DiscordCallback from './components/auth/DiscordCallback.svelte';
  import BrowseTabBar from './components/layout/BrowseTabBar.svelte';
  import SettingsPage from './components/settings/SettingsPage.svelte';
  import UpdateFeedPage from './components/updates/UpdateFeedPage.svelte';
  import WantPage from './components/wantlist/WantPage.svelte';
  import UserPage from './components/user/UserPage.svelte';
  import WantsPage from './components/wants/WantsPage.svelte';

  // Initialize auth on app load (kicks off anonymous sign-in if needed)
  initializeAuth();

  // Preload sidebar data as soon as auth is ready (before user opens sidebar)
  $effect(() => {
    if (authState.ready) {
      loadSidebarData();
    }
  });

  // Load groups when auth is ready and user is signed in.
  // untrack() prevents groupsState.loading from causing infinite re-runs.
  $effect(() => {
    if (authState.ready && !isUserAnonymous()) {
      untrack(() => loadUserGroups());
    }
  });

  let sidebarOpen = $state(false);
</script>

<Sidebar bind:isOpen={sidebarOpen} />

<div class="app">
  {#if route.view === 'search' || route.view === 'binders' || route.view === 'groups' || route.view === 'wants'}
    <BrowseTabBar />
  {/if}

  {#if route.view === 'home'}
    <HomePage />
  {:else if route.view === 'binder'}
    <BinderView />
  {:else if route.view === 'trade-history'}
    <main class="placeholder">
      <h1>Trade History</h1>
      <p>Coming in Phase 2</p>
      <p class="route-debug">Route: {route.view} | Params: {JSON.stringify(route.params)}</p>
    </main>
  {:else if route.view === 'search'}
    <SearchPage />
  {:else if route.view === 'updates'}
    <UpdateFeedPage />
  {:else if route.view === 'groups'}
    <GroupsPage />
  {:else if route.view === 'group-detail'}
    <GroupDetailPage groupId={route.params.groupId} />
  {:else if route.view === 'group-join'}
    <GroupJoinPage groupId={route.params.groupId} token={route.params.token} />
  {:else if route.view === 'binders'}
    <BindersPage />
  {:else if route.view === 'invite'}
    <GroupInvitePage shortCode={route.params.shortCode} />
  {:else if route.view === 'discord-callback'}
    <DiscordCallback code={route.params.code} oauthState={route.params.state} />
  {:else if route.view === 'settings'}
    <SettingsPage />
  {:else if route.view === 'wantlist'}
    <!-- Another owner's list is a fresh page (tabs, sort, open dialogs). -->
    {#key route.params.handle}
      <WantPage handle={route.params.handle} />
    {/key}
  {:else if route.view === 'user'}
    <UserPage handle={route.params.handle} />
  {:else if route.view === 'wants'}
    <WantsPage />
  {/if}
</div>

{#if route.view === 'binder'}
  {#if isTradeMode()}
    <TradeSplitDrawer />
  {:else}
    <SelectedCardsDrawer />
  {/if}
{/if}

<TradeNotification />

<style>
  /* Global reset (replaces Tailwind preflight) */
  :global(*),
  :global(*::before),
  :global(*::after) {
    box-sizing: border-box;
  }

  :global(html) {
    min-height: 100%;
    background: var(--color-bg);
  }

  :global(body) {
    font-family: var(--font-sans);
    color: var(--color-text);
    margin: 0;
  }

  .app {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100vh;
    padding-bottom: 55px;
    box-sizing: border-box;
    /* clip, not hidden: both stop sideways page scroll, but hidden also makes
       .app a scroll container, which breaks every position: sticky bar inside
       it (the page header, the browse tab bar) while the window scrolls. */
    overflow-x: clip;
    position: relative;
  }

  /* Subtle texture overlay matching original */
  .app::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image:
      radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.2) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(180, 160, 120, 0.04) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: #1a1a2e;
    color: #e0e0e0;
  }

  .placeholder h1 {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }

  .placeholder p {
    color: #888;
    margin-bottom: 0.5rem;
  }

  .route-debug {
    font-family: monospace;
    font-size: 0.8rem;
    color: #555;
    margin-top: 1rem;
  }
</style>
